import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, RefreshCw, ShoppingCart } from 'lucide-react';
import { customerPosAPI } from '../../services/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Textarea } from '../ui/textarea';

const money = (value) => (value === null || value === undefined ? '—' : Number(value).toFixed(4));

/** Review the AI-read customer PO, settle rate differences, then raise the Sales Order. */
export const CustomerPoReviewDialog = ({ poId, open, onOpenChange, onCreated }) => {
  const [po, setPo] = useState(null);
  const [lines, setLines] = useState([]);
  const [shipTo, setShipTo] = useState('');
  const [closeInquiry, setCloseInquiry] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    try {
      const response = await customerPosAPI.get(poId);
      const record = response.data;
      setPo(record);
      setShipTo(record.shipping_address || '');
      setLines((record.po_lines || []).map((line) => ({
        ...line,
        include: true,
        quantity: line.po_quantity ?? line.quoted_quantity ?? '',
        price_source: line.price_source || (line.quoted_rate ? 'quotation' : 'po'),
      })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load the Customer PO');
    } finally {
      setLoading(false);
    }
  }, [poId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open || po?.extraction_status !== 'processing') return undefined;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [open, po?.extraction_status, load]);

  const update = (index, patch) => setLines((current) =>
    current.map((line, position) => (position === index ? { ...line, ...patch } : line)));

  const reread = async () => {
    setBusy(true);
    try {
      await customerPosAPI.extract(poId);
      toast.success('Reading the PO again');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not re-read the PO');
    } finally { setBusy(false); }
  };

  const createOrder = async () => {
    setBusy(true);
    try {
      const response = await customerPosAPI.createSalesOrder(poId, {
        lines: lines.map((line) => ({
          include: line.include,
          quotation_line_id: line.quotation_line_id,
          part_id: line.part_id,
          part_internal_no: line.part_internal_no,
          customer_part_no: line.customer_part_no,
          description: line.description,
          quantity: Number(line.quantity) || 0,
          uom: line.po_uom,
          unit_price: line.price_source === 'po' ? line.po_rate : line.quoted_rate,
          price_source: line.price_source,
          net_weight_grams: line.net_weight_grams,
        })),
        ship_to_address: shipTo || null,
        close_inquiry: closeInquiry,
      });
      toast.success(`${response.data.order_number} created from ${po.customer_po_number}`);
      onOpenChange(false);
      onCreated?.(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create the Sales Order');
    } finally { setBusy(false); }
  };

  const status = po?.extraction_status;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" data-testid="customer-po-review-dialog">
        <DialogHeader>
          <DialogTitle>Review Customer PO {po?.customer_po_number}</DialogTitle>
          <DialogDescription>
            Lines read from the customer's document. Settle any rate difference, then raise the Sales Order.
          </DialogDescription>
        </DialogHeader>

        {loading && !po ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" data-testid="po-extraction-status">{status || 'not read'}</Badge>
              {status === 'processing' && <span className="text-sm text-muted-foreground">Reading the document…</span>}
              {status === 'failed' && (
                <span className="flex items-center gap-1 text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4" /> {po?.extraction_error || 'Extraction failed'}
                </span>
              )}
              <Button size="sm" variant="outline" onClick={reread} disabled={busy} data-testid="po-reread-button">
                <RefreshCw className="mr-2 h-4 w-4" /> Read again
              </Button>
            </div>

            <div className="space-y-1">
              <Label>Ship-to address (goes on the sales confirmation)</Label>
              <Textarea rows={2} value={shipTo} onChange={(event) => setShipTo(event.target.value)}
                placeholder="Ship-to address from the PO" data-testid="po-ship-to-input" />
            </div>

            <div className="max-h-[38vh] overflow-auto border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-10" />
                  <TableHead>PO line</TableHead><TableHead>Matched part</TableHead>
                  <TableHead>Qty</TableHead><TableHead>PO rate</TableHead>
                  <TableHead>Quoted rate</TableHead><TableHead>Use</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {lines.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground" data-testid="po-lines-empty">
                      No lines read yet.
                    </TableCell></TableRow>
                  ) : lines.map((line, index) => (
                    <TableRow key={line.id} data-testid={`po-line-${index}`}>
                      <TableCell>
                        <Checkbox checked={line.include} onCheckedChange={(value) => update(index, { include: Boolean(value) })}
                          data-testid={`po-line-include-${index}`} />
                      </TableCell>
                      <TableCell>
                        <p className="font-mono text-sm">{line.customer_part_no || '—'}</p>
                        <p className="text-xs text-muted-foreground">{line.description}</p>
                      </TableCell>
                      <TableCell>
                        {line.matched ? (
                          <Badge className="bg-emerald-100 text-emerald-800 font-mono" data-testid={`po-line-match-${index}`}>
                            {line.part_internal_no || 'matched'}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-900" data-testid={`po-line-unmatched-${index}`}>no match</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input type="number" className="h-8 w-24 font-mono" value={line.quantity}
                          onChange={(event) => update(index, { quantity: event.target.value })}
                          data-testid={`po-line-qty-${index}`} />
                        <span className="text-xs text-muted-foreground">{line.po_uom}</span>
                        {line.quoted_quantity && Number(line.quantity) < line.quoted_quantity && (
                          <p className="text-xs text-amber-700">partial of {line.quoted_quantity}</p>
                        )}
                      </TableCell>
                      <TableCell className="font-mono">{money(line.po_rate)}</TableCell>
                      <TableCell className="font-mono">
                        {money(line.quoted_rate)}
                        {line.rate_variance ? (
                          <span className={`block text-xs ${line.rate_variance > 0 ? 'text-emerald-700' : 'text-red-700'}`}
                            data-testid={`po-line-variance-${index}`}>
                            {line.rate_variance > 0 ? '+' : ''}{money(line.rate_variance)}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant={line.price_source === 'po' ? 'default' : 'outline'}
                            className="h-7 px-2 text-xs" onClick={() => update(index, { price_source: 'po' })}
                            data-testid={`po-line-use-po-${index}`}>PO</Button>
                          <Button size="sm" variant={line.price_source === 'quotation' ? 'default' : 'outline'}
                            className="h-7 px-2 text-xs" disabled={!line.quoted_rate}
                            onClick={() => update(index, { price_source: 'quotation' })}
                            data-testid={`po-line-use-quoted-${index}`}>Quoted</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={closeInquiry} onCheckedChange={(value) => setCloseInquiry(Boolean(value))}
                data-testid="po-close-inquiry-checkbox" />
              Mark the inquiry as won and close it
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="po-review-cancel">Cancel</Button>
          <Button onClick={createOrder} disabled={busy || lines.every((line) => !line.include)}
            data-testid="po-create-sales-order-button">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShoppingCart className="mr-2 h-4 w-4" />}
            Create Sales Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerPoReviewDialog;
